// Custom DS-Table column rendering the Quote Status: a design-system Pill per quote status, plain
// "No quote" text when the project has none (the public column API has no arbitrary-JSX cell, so we
// author a column following the established DS pattern — see StatusPillColumn — and import the two
// cell/column-support helpers from the dist internals, the accepted web-front convention).
//
// The pills are outlined rather than filled because the filled palette has no error colour: Pill's
// `variant` covers highlight/success/neutral/information only, and red lives solely on
// `outlinedVariant`. A declined quote has to read as an error, so every status here uses the
// outlined form to keep one visual style across the column.
import React from 'react';
import type {ColumnDef} from '@tanstack/react-table';
import {Pill, OutlinedPillVariant} from '@forecast-it/design-system';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {defaultColumnDef} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/column/support/BaseColumn';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {StyledCell, Align} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/cell/Cell.styled';
import type {OpportunitySortingFn, OpportunityTableRow} from '../opportunityTableRows';

export interface QuoteStatusColumnType {
	id?: string;
	accessorKey: string;
	header: string;
	visible?: boolean;
	align?: Align;
	'data-cy'?: string;
	allowSorting?: boolean;
	sortingFn?: OpportunitySortingFn;
	/** Localized text for a project without a quote. */
	noQuoteLabel: string;
	width?: string;
}

// Config-only component: DS Table consumes its props via toColumnDef and never renders it.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const QuoteStatusColumn = (_: QuoteStatusColumnType): null => null;

/**
 * The outlined pill variant for a quote status. Undefined means the cell falls back to plain text.
 *
 * All five statuses a quote can hold are mapped (002 D7): without Sent and Revising, a sent quote falls
 * through to the no-quote text and sorts into the wrong group. The label itself comes from the row
 * (`quoteStatusLabel`, resolved once in opportunityTableRows against QUOTE_STATUS_MESSAGE_IDS) so the
 * text a user reads and the text D7 sorts on can never diverge — a status missing from both maps
 * therefore renders, and sorts, as "no quote".
 */
const VARIANT_FOR_STATUS: Record<string, OutlinedPillVariant> = {
	PUBLISHED: OutlinedPillVariant.NEUTRAL,
	// Sent and Revising are both "in flight with the client" — the active outline, distinct from the
	// terminal success/error pair and from a merely published draft.
	SENT: OutlinedPillVariant.ACTIVE,
	REVISING: OutlinedPillVariant.ACTIVE,
	APPROVED: OutlinedPillVariant.SUCCESS,
	DECLINED: OutlinedPillVariant.ERROR,
};

QuoteStatusColumn.toColumnDef = (child: React.ReactElement<QuoteStatusColumnType>): ColumnDef<OpportunityTableRow> => ({
	...defaultColumnDef(child.props),
	accessorKey: child.props.accessorKey,
	sortingFn: child.props.sortingFn,
	cell: ({row}: {row: {original: OpportunityTableRow}}) => {
		const {quoteStatus, quoteStatusLabel} = row.original;
		const variant = quoteStatus ? VARIANT_FOR_STATUS[quoteStatus] : undefined;
		return (
			<StyledCell align={child.props.align} data-cy="opportunity-quote-status-cell">
				{variant && quoteStatusLabel ? (
					<span data-cy="quote-status-pill">
						<Pill outlined outlinedVariant={variant}>
							{quoteStatusLabel}
						</Pill>
					</span>
				) : (
					child.props.noQuoteLabel
				)}
			</StyledCell>
		);
	},
});

export default QuoteStatusColumn;
